import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import { 
  Download, 
  AddCircle, 
  Delete, 
  Visibility, 
  Close 
} from "@mui/icons-material";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { useSelector } from "react-redux";
import moment from "moment";
import InvoiceDocument from "./InvoiceDocument";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- HELPER: BACKWARD TAX CALCULATION ---
const calculateTaxBreakdown = (inclusiveAmount, qty = 1) => {
  const amount = parseFloat(inclusiveAmount) || 0;
  const nights = parseFloat(qty) || 1;
  
  if (amount === 0) return { base: 0, sgst: 0, cgst: 0, taxRate: 0 };

  // 1. Find Price Per Night (Inclusive)
  const pricePerNight = amount / nights;

  // 2. Determine Tax Slab (User Rule: <7500 = 5%, >=7500 = 18%)
  let taxRatePercent = 0;
  if (pricePerNight < 7500) {
    taxRatePercent = 5; 
  } else {
    taxRatePercent = 18;
  }

  // 3. Backward Calculation: Base = Total / (1 + rate)
  const baseAmount = amount / (1 + (taxRatePercent / 100));
  const totalTax = amount - baseAmount;

  return {
    base: baseAmount,
    sgst: totalTax / 2,
    cgst: totalTax / 2,
    taxRate: taxRatePercent
  };
};

const BillingTab = ({ bookingDetails, colors }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  // --- GET HOTEL PROFILE FROM REDUX ---
  const { hotelProfile } = useSelector((state) => ({
      hotelProfile: state.registration.hotelProfile,
      isLoading: state.loading,
  }));

  const [invoiceData, setInvoiceData] = useState({
    guestName: "",
    guestAddress: "",
    guestEmail: "",
    guestMobile: "",
    guestGstin: "",
    invoiceDate: new Date(),
    invoiceNumber: "", // Editable State
  });

  const [lineItems, setLineItems] = useState([]);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (bookingDetails) {
      
      // --- LOGIC: Company > Agent > Guest ---
      let billName = `${bookingDetails?.title || ""} ${bookingDetails?.name || ""}`;
      let billAddress = bookingDetails?.guest_address || "";
      let billEmail = bookingDetails?.email || "";
      let billMobile = bookingDetails?.guest_mobile || "";
      let billGstin = "";

      // 1. Check Company
      if (bookingDetails.company_id && bookingDetails.company_id.name) {
         billName = bookingDetails.company_id.name;
         billAddress = bookingDetails.company_id.address || "";
         billEmail = bookingDetails.company_id.email || "";
         billMobile = bookingDetails.company_id.mobile || "";
         billGstin = bookingDetails.company_id.gst_number || "";
      } 
      // 2. Check Agent (if no company)
      else if (bookingDetails.agent_id && bookingDetails.agent_id.first_name) {
         billName = `${bookingDetails.agent_id.first_name} ${bookingDetails.agent_id.last_name || ""}`;
         billAddress = bookingDetails.agent_id.address || "";
         billEmail = bookingDetails.agent_id.email || "";
         billMobile = bookingDetails.agent_id.mobile || "";
         billGstin = bookingDetails.agent_id.gst_number || "";
      }

      setInvoiceData({
        guestName: billName,
        guestAddress: billAddress,
        guestEmail: billEmail,
        guestMobile: billMobile,
        guestGstin: billGstin,
        invoiceDate: new Date(),
        invoiceNumber: `PI-${bookingDetails.booking_id}`, // Default value
      });

      // --- ITEM GENERATION ---
      const generatedItems = [];

      // 1. Process Rooms
      if (bookingDetails.rooms && bookingDetails.rooms.length > 0) {
        bookingDetails.rooms.forEach((room) => {
          const checkIn = moment.unix(room.checkin_date);
          const checkOut = moment.unix(room.checkout_date);
          const nights = checkOut.diff(checkIn, "days") || 1;
          const roomName = room.room_id?.roomtype_id?.name || `Room ${room.room_id?.room_no}`;
          const totalInclusive = room.total_price || 0;

          const taxData = calculateTaxBreakdown(totalInclusive, nights);

          generatedItems.push({
            id: Math.random(),
            description: `${roomName} (${checkIn.format('DD MMM')} - ${checkOut.format('DD MMM')})`,
            qty: nights,
            inclusiveRate: (totalInclusive / nights).toFixed(2),
            inclusiveAmount: totalInclusive,
            ...taxData
          });
        });
      }

      // 2. Adjustments
      const currentTotal = generatedItems.reduce((sum, item) => sum + Number(item.inclusiveAmount), 0);
      const bookingTotal = bookingDetails.custom_price || bookingDetails.total_price;
      
      if (bookingTotal > currentTotal) {
         const diff = bookingTotal - currentTotal;
         const taxData = calculateTaxBreakdown(diff, 1);
         
         generatedItems.push({
            id: Math.random(),
            description: "Service Charges / Adjustments",
            qty: 1,
            inclusiveRate: diff.toFixed(2),
            inclusiveAmount: diff,
            ...taxData
         });
      }
      setLineItems(generatedItems);
    }
  }, [bookingDetails]);

  // --- HANDLERS ---
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData({ ...invoiceData, [name]: value });
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;

    if (field === "inclusiveAmount" || field === "qty") {
        const amt = parseFloat(newItems[index].inclusiveAmount) || 0;
        const qty = parseFloat(newItems[index].qty) || 1;
        newItems[index].inclusiveRate = (amt / qty).toFixed(2);
        const taxData = calculateTaxBreakdown(amt, qty);
        newItems[index] = { ...newItems[index], ...taxData };
    }
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { 
        id: Math.random(), 
        description: "Extra Service", 
        qty: 1, 
        inclusiveRate: 0, 
        inclusiveAmount: 0,
        base: 0, sgst: 0, cgst: 0, taxRate: 0
    }]);
  };

  const removeLineItem = (index) => {
    const newItems = [...lineItems];
    newItems.splice(index, 1);
    setLineItems(newItems);
  };

  const grandTotal = useMemo(() => {
    return lineItems.reduce((acc, item) => acc + (parseFloat(item.inclusiveAmount) || 0), 0).toFixed(2);
  }, [lineItems]);

  return (
    <Box>
      {/* TOOLBAR */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">
          Proforma Invoice
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={isEditing}
                onChange={() => setIsEditing(!isEditing)}
                color="secondary"
              />
            }
            label={<Typography variant="body2">Enable Editing</Typography>}
          />
          <Button 
            variant="outlined" 
            color="info" 
            startIcon={<Visibility />}
            onClick={() => setOpenPreview(true)}
          >
            Preview
          </Button>
          <PDFDownloadLink
            document={
              <InvoiceDocument
                invoiceData={invoiceData}
                items={lineItems}
                bookingDetails={bookingDetails}
                hotelProfile={hotelProfile}
              />
            }
            fileName={`PI_${bookingDetails?.booking_id}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Download />}
                disabled={loading}
              >
                {loading ? "Generating..." : "Download PDF"}
              </Button>
            )}
          </PDFDownloadLink>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* LEFT: Header Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: colors.primary[500], height: "100%" }}>
            
            {/* 1. INVOICE DETAILS SECTION */}
            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>Invoice Details</Typography>
            <Stack spacing={2} mb={3}>
                <TextField 
                    label="Invoice Number" 
                    name="invoiceNumber" 
                    value={invoiceData.invoiceNumber} 
                    onChange={handleHeaderChange} 
                    disabled={!isEditing} 
                    size="small" 
                    fullWidth 
                    helperText={isEditing ? "You can manually override the Invoice ID" : ""}
                />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {/* 2. BILL TO DETAILS SECTION */}
            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>Bill To Details</Typography>
            <Stack spacing={2}>
              <TextField label="Name" name="guestName" value={invoiceData.guestName} onChange={handleHeaderChange} disabled={!isEditing} size="small" fullWidth />
              <TextField label="Address" name="guestAddress" value={invoiceData.guestAddress} onChange={handleHeaderChange} disabled={!isEditing} multiline rows={3} size="small" fullWidth />
              <TextField label="Mobile" name="guestMobile" value={invoiceData.guestMobile} onChange={handleHeaderChange} disabled={!isEditing} size="small" fullWidth />
              <TextField label="GSTIN" name="guestGstin" value={invoiceData.guestGstin} onChange={handleHeaderChange} disabled={!isEditing} size="small" fullWidth placeholder="Optional" />
            </Stack>
          </Paper>
        </Grid>

        {/* RIGHT: Editable Table */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, backgroundColor: colors.primary[500], overflow: "hidden" }}>
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color={colors.greenAccent[500]}>Invoice Items</Typography>
                {isEditing && (
                    <Button startIcon={<AddCircle />} size="small" onClick={addLineItem} color="secondary">Add Item</Button>
                )}
            </Box>
            
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="40%">Description</TableCell>
                    <TableCell width="10%" align="center">Qty</TableCell>
                    <TableCell width="25%" align="center">Total Amount <br/><small>(Inc. Tax)</small></TableCell>
                    <TableCell width="15%" align="center">Tax %</TableCell>
                    {isEditing && <TableCell width="10%">Action</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {isEditing ? (
                          <TextField value={item.description} onChange={(e) => handleLineItemChange(index, "description", e.target.value)} fullWidth variant="standard" InputProps={{ disableUnderline: true }} />
                        ) : <Typography variant="body2">{item.description}</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <TextField value={item.qty} type="number" onChange={(e) => handleLineItemChange(index, "qty", e.target.value)} fullWidth inputProps={{ style: { textAlign: 'center' } }} variant="standard" />
                        ) : item.qty}
                      </TableCell>
                      <TableCell align="center">
                         {isEditing ? (
                          <TextField value={item.inclusiveAmount} type="number" onChange={(e) => handleLineItemChange(index, "inclusiveAmount", e.target.value)} fullWidth inputProps={{ style: { textAlign: 'center', fontWeight: 'bold' } }} variant="standard" />
                        ) : <Typography fontWeight="bold">₹{item.inclusiveAmount}</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="textSecondary">{item.taxRate}%</Typography>
                      </TableCell>
                      {isEditing && (
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeLineItem(index)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box p={2} borderTop={`1px solid ${colors.grey[700]}`} bgcolor={colors.primary[600]}>
                <Stack direction="row" justifyContent="flex-end" spacing={4} alignItems="center">
                    <Typography>Grand Total:</Typography>
                    <Typography variant="h4" color={colors.greenAccent[500]} fontWeight="bold">₹ {grandTotal}</Typography>
                </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* PREVIEW MODAL */}
      <Dialog fullScreen open={openPreview} onClose={() => setOpenPreview(false)} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative', backgroundColor: colors.primary[500] }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setOpenPreview(false)} aria-label="close"><Close /></IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Invoice Preview</Typography>
            <Button autoFocus color="inherit" onClick={() => setOpenPreview(false)}>Close</Button>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 0, height: '100%', backgroundColor: '#525659' }}>
           <PDFViewer width="100%" height="100%" style={{ border: "none" }}>
              <InvoiceDocument 
                invoiceData={invoiceData} 
                items={lineItems} 
                bookingDetails={bookingDetails} 
                hotelProfile={hotelProfile}
              />
           </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BillingTab;